import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../_metronic/helpers/utils/api';

interface LinkInputProps {
  className?: string;
  id: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** Direct value setter for when we programmatically set the value */
  onValueChange?: (value: string) => void;
}

interface PageOption {
  id: number;
  name: string;
  slug: string;
}

const STANDARD_PAGES = [
  'home', 'career', 'contact', 'loan', 'loans', 'privacy', 'faq', 'business',
  'template', 'about', 'blog', 'resource', 'blogdetail', 'navbar', 'footer',
  'digitization', 'mentorship', 'financialliteracy', 'grants', 'getinvolved',
  'corporate', 'successstories', 'walktogethertour', 'programs'
];

let _cachedPages: PageOption[] | null = null;
let _cacheTime = 0;
const CACHE_TTL = 30_000; // 30s

async function loadPages(): Promise<PageOption[]> {
  const now = Date.now();
  if (_cachedPages && now - _cacheTime < CACHE_TTL) return _cachedPages;

  try {
    const response = await api.get('page');
    if (response.data && Array.isArray(response.data.allPages)) {
      const backendPages = response.data.allPages;
      const customPages = backendPages.filter((p: any) => !STANDARD_PAGES.includes(String(p.name).toLowerCase()));

      // Fetch titles
      const titlesRes = await api.get('data', { params: { sectionName: 'PageMetadata', key: 'title' } });
      const titlesData = titlesRes.data?.data || [];

      const mapped: PageOption[] = customPages.map((p: any) => {
        const titleRecord = titlesData.find((d: any) => d.pageId === p.id);
        return {
          id: p.id,
          slug: p.name,
          name: titleRecord?.valueEnglish || p.name,
        };
      });

      _cachedPages = mapped;
      _cacheTime = now;
      return mapped;
    }
  } catch (e) {
    console.error('Failed to load pages for link input', e);
  }
  return [];
}

const LinkInput: React.FC<LinkInputProps> = ({
  className,
  id,
  placeholder,
  value,
  onChange,
  onValueChange,
}) => {
  const [pages, setPages] = useState<PageOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = async () => {
    if (showDropdown) {
      setShowDropdown(false);
      return;
    }
    setIsLoading(true);
    const loadedPages = await loadPages();
    setPages(loadedPages);
    setIsLoading(false);
    setShowDropdown(true);
  };

  const handleSelect = (page: PageOption) => {
    const url = `/${page.slug}`;
    if (onValueChange) {
      onValueChange(url);
    } else {
      // Synthetic event approach
      const nativeInput = document.getElementById(id) as HTMLInputElement | null;
      if (nativeInput) {
        const nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        if (nativeSetter) {
          nativeSetter.call(nativeInput, url);
          nativeInput.dispatchEvent(new Event('input', { bubbles: true }));
          nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '0', width: '100%' }}>
      <input
        className={className}
        id={id}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, flex: 1 }}
      />
      <button
        type="button"
        onClick={handleToggle}
        title="Select a page"
        style={{
          height: '100%',
          padding: '0 10px',
          border: '1px solid #E4E6EF',
          borderLeft: 'none',
          borderTopRightRadius: '6px',
          borderBottomRightRadius: '6px',
          background: showDropdown ? '#EEF6FF' : '#F5F8FA',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'background 0.15s',
          minHeight: '38px',
        }}
      >
        {isLoading ? (
          <span className="spinner-border spinner-border-sm" style={{ width: '14px', height: '14px' }}></span>
        ) : (
          <i className="bi bi-file-earmark-text" style={{ fontSize: '16px', color: '#3E97FF' }}></i>
        )}
      </button>

      {showDropdown && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            zIndex: 1000,
            width: '280px',
            maxHeight: '240px',
            overflowY: 'auto',
            marginTop: '4px',
            backgroundColor: '#fff',
            borderRadius: '10px',
            boxShadow: '0 10px 40px -8px rgba(0,0,0,0.18)',
            border: '1px solid #EFF2F5',
            animation: 'cmsModalSlideUp 0.15s ease',
          }}
        >
          <div style={{ padding: '10px 14px 6px', borderBottom: '1px solid #EFF2F5' }}>
            <span style={{ fontWeight: 700, fontSize: '12px', color: '#A1A5B7', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Custom Pages
            </span>
          </div>
          {pages.length === 0 ? (
            <div style={{ padding: '16px', textAlign: 'center', color: '#A1A5B7', fontSize: '13px' }}>
              No custom pages created yet.
            </div>
          ) : (
            pages.map((page) => (
              <button
                key={page.id}
                type="button"
                onClick={() => handleSelect(page)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 14px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 0.1s',
                  fontSize: '13px',
                }}
                onMouseEnter={(e) => { (e.target as HTMLElement).style.backgroundColor = '#F5F8FA'; }}
                onMouseLeave={(e) => { (e.target as HTMLElement).style.backgroundColor = 'transparent'; }}
              >
                <i className="bi bi-file-earmark" style={{ color: '#3E97FF', fontSize: '14px', flexShrink: 0 }}></i>
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontWeight: 600, color: '#181C32', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{page.name}</span>
                  <span style={{ color: '#A1A5B7', fontSize: '11px' }}>/{page.slug}</span>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default LinkInput;
